import { useMutation } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import {
    ListingDetailCatalogInput,
    ListingDetailDocumentInput,
} from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import FilestackButton, {
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import PublishIcon from '@material-ui/icons/Publish'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    container: { maxWidth: 368 },
    fullWidth: {
        width: '100%',
    },
    documentTitle: {
        color: '#5E5F61',
        textTransform: 'uppercase',
    },
    documentFileContainer: { height: 40, paddingLeft: theme.spacing(2) },
    documentFile: {
        ...theme.typography.h6,
    },
    documentButtons: {
        paddingRight: `0 !important`,
        paddingTop: `${theme.spacing(2.72)}px !important`,
    },
    uploadBtnContainer: {
        paddingRight: theme.spacing(0.5),
    },
    iconButton: {
        height: 36,
        width: 34,
        padding: 0,
        margin: 0,
        '& .MuiIconButton-label': {
            height: '100%',
        },
        '& .MuiIcon-root': {
            height: 'unset',
            width: 'unset',
        },
    },
    withBorder: {
        borderBottom: '1px solid #04325B',
    },
    withoutBorder: {
        borderBottom: '1px solid #FFFFFF',
    },
    withTopPadding: {
        paddingTop: theme.spacing(1),
    },
    buttonContainer: {
        borderBottom: '1px solid #FFFFFF',
        '& button.MuiButton-contained': {
            backgroundColor: '#FFFFFF',
            borderRadius: 50,
        },
        paddingRight: `0 !important`,
        paddingTop: `${theme.spacing(1.5)}px !important`,
    },
    buttonText: {
        ...theme.typography.h6,
        color: '#04325B',
    },
    loadingSkeleton: {
        width: '80%',
    },
}))

interface EditDocumentRowProps {
    text: string
    last?: boolean
    handleChange?: (
        value: Partial<ListingDetailDocumentInput | ListingDetailCatalogInput>
    ) => void
    value: Partial<ListingDetailDocumentInput | ListingDetailCatalogInput>
    handleFilestackLoading?: (filestackLoading: boolean | null) => void
    transferringFiles: boolean
    handleTransferringFiles?: (value: boolean, uploadDone?: boolean) => void
    disableButtons?: boolean
}

export const EditDocumentRow: React.FC<EditDocumentRowProps> = ({
    text,
    last,
    handleChange,
    value,
    handleFilestackLoading,
    transferringFiles,
    handleTransferringFiles,
    disableButtons,
}) => {
    const classes = useStyles()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [showForm, setShowForm] = React.useState<boolean>(value.title !== '')

    const key = text.replace(/\s/g, '')

    const handleShowForm = () => {
        setShowForm(true)
    }

    const handleClearForm = () => {
        handleChange?.({
            ...value,
            title: '',
            id: key === 'catalog' ? undefined : '',
        })
    }

    const onFilepickerDone = (pickerResponse: PickerResponse) => {
        handleTransferringFiles?.(false, true)
        if (pickerResponse.filesFailed.length) {
            showGlobalSnackbar?.({
                variables: {
                    id: `${text.split(' ').join()}FileUploadError`,
                    message: `Error uploading ${text}.`,
                    variant: 'error',
                },
            })
            return
        }

        handleChange?.({
            docType:
                key === 'catalog'
                    ? undefined
                    : (value as ListingDetailDocumentInput).docType,
            title: pickerResponse.filesUploaded[0].filename.substr(0, 200),
            fileUploadDocument: {
                fileName: `${pickerResponse.filesUploaded[0].key}`,
                fileNameTarget: `${pickerResponse.filesUploaded[0].filename}`,
            },
        })
    }

    return (
        <TableRow className="documentType">
            {!showForm ? (
                <TableCell
                    padding="none"
                    className={clsx(
                        classes.buttonContainer,
                        last ? classes.withoutBorder : classes.withBorder
                    )}
                >
                    <RoundButton
                        hasShadow={false}
                        borderRadius={50}
                        color="#04325B"
                        borderColor="#04325B"
                        backgroundColor="#FFF"
                        data-testid={`btnShowForm${key}`}
                        onClick={handleShowForm}
                    >
                        <Typography
                            className={classes.buttonText}
                            component="span"
                        >
                            {`Upload ${text}`}
                        </Typography>
                    </RoundButton>
                </TableCell>
            ) : (
                <>
                    <TableCell
                        padding="none"
                        className={clsx(
                            classes.container,
                            classes.withTopPadding,
                            last ? classes.withoutBorder : classes.withBorder
                        )}
                    >
                        <GridContainer direction="column">
                            <GridItem>
                                <Typography
                                    variant="subtitle2"
                                    className={classes.documentTitle}
                                >
                                    {text}
                                </Typography>
                            </GridItem>
                            <GridContainer
                                item
                                className={classes.documentFileContainer}
                                justify="flex-start"
                                alignItems="flex-end"
                            >
                                {transferringFiles ? (
                                    <Skeleton
                                        variant="rect"
                                        data-testid={`skeleton${key}`}
                                        className={classes.loadingSkeleton}
                                    />
                                ) : (
                                    <SingleLineEllipsis
                                        className={clsx(
                                            'dvDocumentFile',
                                            classes.documentFile
                                        )}
                                        text={
                                            value.title
                                                ? value.title
                                                : 'Name of the document goes here.pdf'
                                        }
                                    />
                                )}
                            </GridContainer>
                        </GridContainer>
                    </TableCell>
                    <TableCell
                        padding="none"
                        width="20%"
                        className={clsx(
                            classes.documentButtons,
                            last ? classes.withoutBorder : classes.withBorder
                        )}
                    >
                        <GridContainer justify="flex-end" alignItems="flex-end">
                            <GridItem className={classes.uploadBtnContainer}>
                                <FilestackButton
                                    key={`filestack${text.split(' ')[0]}`}
                                    apiKey={
                                        processEnv.REACT_APP_FileStack_ApiKey
                                    }
                                    pickerOptions={{
                                        accept: ['pdf', 'doc', 'docx'],
                                        maxSize: 2097152000,
                                        onClose: () =>
                                            handleTransferringFiles?.(
                                                false,
                                                true
                                            ),
                                        onFileSelected: (file) => {
                                            if (!file.size || file.size === 0) {
                                                throw new Error(
                                                    'File has 0KB size, upload file with more than 0KB size.'
                                                )
                                            }
                                        },
                                    }}
                                    kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                                    showGlobalSnackbar={showGlobalSnackbar}
                                    handleTransferringFiles={
                                        handleTransferringFiles
                                    }
                                    onFilepickerDone={onFilepickerDone}
                                    handleLoadingFilestack={
                                        handleFilestackLoading
                                    }
                                    renderButton={(
                                        onClick: (
                                            event: React.MouseEvent
                                        ) => void
                                    ) => (
                                        <IconButton
                                            aria-label="upload"
                                            className={classes.iconButton}
                                            onClick={onClick}
                                            data-testid={`btnUpload${key}`}
                                            disabled={disableButtons}
                                        >
                                            <PublishIcon
                                                style={{ color: '#27AE60' }}
                                            />
                                        </IconButton>
                                    )}
                                />
                            </GridItem>
                            <GridItem>
                                <IconButton
                                    aria-label="delete"
                                    onClick={handleClearForm}
                                    className={classes.iconButton}
                                    data-testid={`btnClear${key}`}
                                    disabled={disableButtons}
                                >
                                    <DeleteIcon color="primary" />
                                </IconButton>
                            </GridItem>
                        </GridContainer>
                    </TableCell>
                </>
            )}
        </TableRow>
    )
}
